import React, { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { useQuery } from "react-apollo";

import { useRouter } from "../../hooks/useRouter";
import { useNotification } from "../NotificationContext/NotificationContext";

import { GET_SINGLE_PRODUCTS } from "../../../queries/archive";

import {
  ProductSingleContext,
  ProductSingleContextType,
} from "./ProductSingleContext";
import { useProductSingleHelpers } from "../../../components/lib/useProductSingleHelpers";

interface ProductSingleContextProviderProps {
  children: React.ReactNode | null;
}

export interface SelectedAttributes {
  attribute: string;
  value: string;
}

export const ProductSingleContextProvider = (
  props: ProductSingleContextProviderProps
) => {
  const { query } = useRouter();
  const notificationCtx = useNotification();
  const activeFilter = useRef<any>(undefined);
  const {
    getProductAttributes,
    getProductVariations,
  } = useProductSingleHelpers();

  const { loading, error, data } = useQuery(GET_SINGLE_PRODUCTS, {
    fetchPolicy: "no-cache",
    variables: {
      slug: Array.isArray(query.slug)
        ? query.slug
        : query.slug
        ? [query.slug]
        : [],
    },
  });

  const product = data?.products?.nodes[0];

  const productSpecifications = product?.productSpecifications ?? {};

  const [quantity, setQuantity] = useState(1);
  const [inStock, setInStock] = useState(true);
  const [variations, setVariations] = useState([]);
  const [attributes, setAttributes] = useState<any>([]);
  const [variation, setVariation] = useState<any>(undefined);

  const [selectedAttributes, setSelectedAttributes] = useState<
    SelectedAttributes[]
  >([]);

  useEffect(() => {
    initProductStates();

    return () => {
      clearProductStates();
    };
  }, [product]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    initProductAttributes();
  }, [variations]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleVariationUpdate();
    handleAttributesFilter();
    notificationCtx.removeAll();
  }, [selectedAttributes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleOnVariationUpdate();
  }, [variation]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearProductStates = () => {
    setQuantity(1);
    setInStock(true);

    // Variable product
    setAttributes([]);
    setVariations([]);
    setVariation(undefined);
    setSelectedAttributes([]);
    activeFilter.current = undefined;
  };

  const initProductStates = () => {
    if (!product) {
      return;
    }

    setInStock(() => {
      if (product.type === "SIMPLE") {
        return product.stockStatus === "IN_STOCK" && product.stockQuantity >= 1;
      }

      return product.stockStatus === "IN_STOCK";
    });

    if (product.type === "VARIABLE" && product.variations?.nodes.length > 0) {
      setVariations(getProductVariations(product.variations?.nodes));
    }
  };

  const initProductAttributes = () => {
    if (variations.length === 0) {
      return;
    }

    // const getAttributes = getProductAttributes(variations);

    // getAttributes.forEach((item: any) => {
    //   const values = Array.from(item.values);

    //   if (values.length === 1) {
    //     setSelectedAttributes([
    //       ...selectedAttributes,
    //       {
    //         attribute: item.name,
    //         value: values[0] as string
    //       }
    //     ]);
    //   }
    // });

    setAttributes(getProductAttributes(variations));
  };

  const getVariationsOfActiveFilter = () => {
    return variations.filter((item: any) => {
      return !!item.attributes.find(
        (i: any) =>
          i.attribute === activeFilter.current.name &&
          activeFilter.current.value === i.value
      );
    });
  };

  const handleVariationUpdate = () => {
    if (attributes.length === 0) {
      return;
    }

    if (selectedAttributes.length < attributes.length) {
      setVariation(undefined);
      return;
    }

    function compareArrays(arr1: any, arr2: any) {
      if (arr1.length !== arr2.length) {
        return null;
      }

      const sortedArr1 = arr1.sort((a: any, b: any) =>
        JSON.stringify(a) > JSON.stringify(b) ? 1 : -1
      );
      const sortedArr2 = arr2.sort((a: any, b: any) =>
        JSON.stringify(a) > JSON.stringify(b) ? 1 : -1
      );

      for (let i = 0; i < sortedArr1.length; i++) {
        if (JSON.stringify(sortedArr1[i]) !== JSON.stringify(sortedArr2[i])) {
          return null;
        }
      }

      return sortedArr1;
    }

    const newActive = _.find(variations, (o: any) => {
      return compareArrays(o.attributes, selectedAttributes);
    });

    setVariation(newActive);
  };

  const handleAttributesFilter = () => {
    const selectedAttrLength = selectedAttributes.length;

    if (selectedAttrLength === 0) {
      setAttributes(getProductAttributes(variations));
      return;
    }

    /*
      E.g. Get all variations that in attribute Size have M selected.
    */
    const filteredVariations = getVariationsOfActiveFilter();

    /*
      Step 1: Get all attributes from the filtered variations above.
      Step 2: Filter the current attribute from the array of the attributes.

      This variable will contain all the filtered attributes, except the current one which was used for filtering.
    */
    const filteredAttributes = getProductAttributes(filteredVariations).filter(
      (item: any) => item.name !== activeFilter.current.name
    );

    /*
      Get the current version of the attribute which was used for filtering.
    */
    const currentAttribute: any = attributes.filter(
      (item: any) => item.name === activeFilter.current.name
    );

    /*
      Merge the filtered attributes with the current one.
    */
    const newAttributes = [
      ...filteredAttributes,
      {
        name: currentAttribute[0].name,
        values: currentAttribute[0].values,
      },
    ];

    setAttributes(_.orderBy(newAttributes, ["name"], ["asc"]));
  };

  const handleOnVariationUpdate = () => {
    if (product?.type !== "VARIABLE") {
      return;
    }

    setQuantity(1);

    if (!variation) {
      setInStock(() => {
        if (product.type === "SIMPLE") {
          return (
            product.stockStatus === "IN_STOCK" && product.stockQuantity >= 1
          );
        }

        return product.stockStatus === "IN_STOCK";
      });
      return;
    }

    setInStock(variation.stockStatus === "IN_STOCK");
  };

  const updateSelectedAttributes = (key: string, value: string) => {
    activeFilter.current = { name: key, value };

    setSelectedAttributes((prev) => {
      let newValue = prev;

      // Remove the previous value of the attribute if it was set.
      _.remove(newValue, (item) => item.attribute === key);

      // When clicking default option(which doesn't have value), don't merge that value to the array.
      if (value === "") {
        return _.orderBy(newValue, ["attribute"], ["asc"]);
      }

      newValue = [
        ...newValue,
        {
          attribute: key,
          value,
        },
      ];

      // Alphabetically order the attributes to make it possible to check for its variations.
      return _.orderBy(newValue, ["attribute"], ["asc"]);
    });
  };

  const getDisabledOptions = (
    selectedAttribute: string,
    currentValues?: string[]
  ) => {
    if (
      activeFilter.current === undefined ||
      activeFilter.current["name"] === selectedAttribute
    ) {
      return currentValues || [];
    }

    const values = new Set<any>();

    const filters = getVariationsOfActiveFilter();

    filters.forEach((item: any) => {
      if (item["stockStatus"] === "IN_STOCK") {
        return;
      }

      const variation = item["attributes"].find((a: any) => {
        return a["attribute"] === selectedAttribute;
      });

      if (variation) {
        values.add(variation.value);
      }
    });

    return Array.from(values);
  };

  const emptySelectedAttributes = () => {
    setVariation(undefined);
    setSelectedAttributes([]);
    setAttributes(getProductAttributes(variations));
  };

  const context: ProductSingleContextType = {
    loading,
    error,
    inStock,
    product,
    quantity,
    variation,
    attributes,
    setInStock,
    setQuantity,
    setVariation,
    selectedAttributes,
    getDisabledOptions,
    emptySelectedAttributes,
    productSpecifications,
    updateSelectedAttributes,
    staticAttributes: product?.attributes?.nodes.filter(
      (productAttributes: any) => {
        return !attributes.find(
          (item: any) => item.name === productAttributes.attributeName
        );
      }
    ),
  };

  return (
    <ProductSingleContext.Provider value={context}>
      {props.children}
    </ProductSingleContext.Provider>
  );
};
